/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import React, { useEffect } from 'react';
import {
  BackstageIdentityResponse,
  ConfigApi,
  configApiRef,
  SignInPageProps,
  useApi,
} from '@backstage/core-plugin-api';
import Typography from '@material-ui/core/Typography';
import { ComponentType, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Page,
  Progress,
  SignInProviderConfig,
  UserIdentity,
} from '@backstage/core-components';
import { Box, Divider } from '@material-ui/core';
import {
  StyledContent,
  StyledLink,
  StyledLogoFull,
  StyledButton,
  Footer,
  FooterText,
  FooterLinks,
  FooterLink,
} from './CustomSignInPage.styles';

type CommonSignInPageProps = SignInPageProps & {
  /**
   * Error component to be rendered instead of the default error panel in case
   * sign in fails.
   */
  ErrorComponent?: ComponentType<{ error?: Error }>;
};

type SingleSignInPageProps = CommonSignInPageProps & {
  provider: SignInProviderConfig;
  auto?: boolean;
};

export const SignInPage = ({
  provider,
  auto,
  onSignInSuccess,
  ErrorComponent,
}: SingleSignInPageProps) => {
  const authApi = useApi(provider.apiRef);
  const configApi: ConfigApi = useApi(configApiRef);
  const [error, setError] = useState<Error>();

  // The SignIn component takes some time to decide whether the user is logged-in or not.
  // showLoginPage is used to prevent a glitch-like experience where the sign-in page is
  // displayed for a split second when the user is already logged-in.
  const [showLoginPage, setShowLoginPage] = useState<boolean>(false);

  // User was redirected back to sign in page with error from auth redirect flow
  const [searchParams, _setSearchParams] = useSearchParams();
  const errorParam = searchParams.get('error');

  type LoginOpts = { checkExisting?: boolean; showPopup?: boolean };
  const login = async ({ checkExisting, showPopup }: LoginOpts) => {
    try {
      let identityResponse: BackstageIdentityResponse | undefined;
      if (checkExisting) {
        // Do an initial check if any logged-in session exists
        identityResponse = await authApi.getBackstageIdentity({
          optional: true,
        });
      }

      // If no session exists, show the sign-in page
      if (!identityResponse && (showPopup || auto) && !errorParam) {
        // Unless auto is set to true, this step should not happen.
        // When user intentionally clicks the Sign In button, autoShowPopup is set to true
        setShowLoginPage(true);
        identityResponse = await authApi.getBackstageIdentity({
          instantPopup: true,
        });
        if (!identityResponse) {
          throw new Error(
            `The ${provider.title} provider is not configured to support sign-in`,
          );
        }
      }

      if (!identityResponse) {
        setShowLoginPage(true);
        return;
      }

      const profile = await authApi.getProfile();
      onSignInSuccess(
        UserIdentity.create({
          identity: identityResponse.identity,
          authApi,
          profile,
        }),
      );
    } catch (err: any) {
      // User closed the sign-in modal
      setError(err);
      setShowLoginPage(true);
    }
  };

  useEffect(() => {
    if (errorParam) {
      setError(new Error(errorParam));
    }
    login({ checkExisting: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRegister = () => {
    const registrationUrl = configApi.getString('registration.url');
    window.location.href = registrationUrl;
  };

  return showLoginPage ? (
    <Page themeId="home">
      <StyledContent>
        <Box
          sx={{
            width: 404,
            bgcolor: 'white',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gridGap: 24,
            justifyContent: 'center',
          }}
        >
          <StyledLogoFull />
          <Divider style={{ width: '100%' }} />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gridGap: 12,
              textAlign: 'center',
            }}
          >
            <Typography variant="h6">Welcome to DX Portal</Typography>
            <Typography variant="body1">
              Please sign in to continue or
              <br /> create a new account
            </Typography>
          </Box>

          <StyledButton
            variant="contained"
            color="primary"
            onClick={handleRegister}
          >
            Register
          </StyledButton>

          <Divider style={{ width: '100%' }} />

          {error &&
            error.name !== 'PopupRejectedError' &&
            (ErrorComponent ? (
              <ErrorComponent error={error} />
            ) : (
              <Typography variant="body1" color="error">
                {error.message?.replace(/: \b\S+:\S+\b\.?/g, '')}
              </Typography>
            ))}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gridGap: 0.5,
            }}
          >
            <Typography variant="caption">
              Already have an account?
            </Typography>

            <StyledLink
              onClick={() => {
                login({ showPopup: true });
              }}
            >
              Sign in
            </StyledLink>
          </Box>
        </Box>
        <Footer>
          <FooterText>
            CUSTOMER SERVICE: Australia: +61 383611501 | Europe: +8000 183 909,
            +1 862 277 8813 | Hong Kong: +852 22817502 | Japan: +81-726299066
            (English), +81-727368473 (Japanese) | North America: +1 877 357 7782
            | Singapore: +65 65053912
          </FooterText>
          <FooterLinks>
            <FooterLink href="mailto:customercare@digitalrealty.com">
              customercare@digitalrealty.com
            </FooterLink>
          </FooterLinks>
          <FooterLinks>
            <FooterLink
              href="https://www.digitalrealty.com/about/legal/policies/acceptable-use"
              target="_blank"
              rel="noreferrer"
            >
              Acceptable Use Policy
            </FooterLink>
            <span>-</span>
            <FooterLink
              href="https://www.digitalrealty.com/about/legal/privacy"
              target="_blank"
              rel="noreferrer"
            >
              Privacy
            </FooterLink>
          </FooterLinks>
        </Footer>
      </StyledContent>
    </Page>
  ) : (
    <Progress />
  );
};
